import type { QueueItem, QueueSessionEnvelope, QueueSessionSummary } from './queueApi';
import type { QueueRealtimeEvent } from '../queue/types';
import type { ProgressSummary } from './useQueueProgressReporter';

type QueueSnapshot = {
  session: QueueSessionSummary | null;
  items: QueueItem[];
};

type QueueSnapshotProvider = () => QueueSnapshot | null | undefined;
type ProgressSummaryProvider = () => ProgressSummary | null;
type NumberProvider = () => number;

type NullableStateProvider<T> = () => T | null;
type LastPlayingState = { positionMs: number; trackUri: string | null; updatedAt: number };

type LogLevel = 'info' | 'warn' | 'error';

type BaseOptions = {
  enabled: boolean;
  getSnapshot: QueueSnapshotProvider;
  getProgressSummary?: ProgressSummaryProvider;
  getProgressUpdatedAt?: NumberProvider;
  getLastPlayingState?: NullableStateProvider<LastPlayingState>;
};

type EventDetails = Record<string, unknown>;

type EnvelopeExtraDetails = EventDetails | undefined;

export type QueueDiagnostics = {
  logInfo: (event: string, details?: EventDetails) => void;
  logWarn: (event: string, details?: EventDetails) => void;
  logError: (event: string, details?: EventDetails) => void;
  logEnvelope: (source: string, envelope: QueueSessionEnvelope, details?: EnvelopeExtraDetails) => void;
  logRealtime: (event: QueueRealtimeEvent) => void;
};

const NAMESPACE = '[QueueDiag]';

const toSnapshot = (snapshot?: QueueSnapshot | null): QueueSnapshot => {
  if (snapshot && typeof snapshot === 'object') {
    return {
      session: snapshot.session ?? null,
      items: Array.isArray(snapshot.items) ? snapshot.items : []
    };
  }

  return { session: null, items: [] };
};

const clampIndex = (index: unknown, size: number): number | null => {
  if (size <= 0) return null;
  if (typeof index !== 'number' || !Number.isFinite(index)) return 0;
  if (size === 1) return 0;
  const clamped = Math.min(size - 1, Math.max(0, Math.floor(index)));
  return clamped;
};

const findItemById = (items: QueueItem[], id: string | null): QueueItem | null => {
  if (!id) return null;
  return items.find((item) => item.id === id) ?? null;
};

const derivePointer = (session: QueueSessionSummary | null, items: QueueItem[]) => {
  if (!session) {
    return {
      pointerIndex: null as number | null,
      pointerItem: null as QueueItem | null,
      nextItem: null as QueueItem | null
    };
  }

  const pointerIndex = clampIndex(session.currentItemIndex, items.length);
  const pointerById = findItemById(items, session.currentItemId);
  const pointerItem = pointerById ?? (pointerIndex != null ? items[pointerIndex] ?? null : null);

  const nextIndex =
    pointerIndex != null && pointerIndex + 1 < items.length ? pointerIndex + 1 : null;
  const nextItem = nextIndex != null ? items[nextIndex] ?? null : null;

  return {
    pointerIndex,
    pointerItem,
    nextItem
  };
};

const extractPointerSnapshot = (snapshot: QueueSnapshot) => {
  const { session, items } = snapshot;
  const { pointerIndex, pointerItem, nextItem } = derivePointer(session, items);

  return {
    sessionId: session?.id ?? null,
    sessionRevision: session?.revision ?? null,
    sessionStatus: session?.status ?? null,
    sessionDeviceId: session?.deviceId ?? null,
    pointerIndex,
    pointerItemId: pointerItem?.id ?? session?.currentItemId ?? null,
    pointerTrackUri: pointerItem?.trackUri ?? null,
    pointerMatchesSessionId:
      pointerItem && session?.currentItemId
        ? pointerItem.id === session.currentItemId
        : Boolean(pointerItem) === Boolean(session?.currentItemId),
    queueLength: items.length,
    nextItemId: nextItem?.id ?? null,
    nextTrackUri: nextItem?.trackUri ?? null
  };
};

const extractEnvelopePointer = (envelope: QueueSessionEnvelope) => {
  const { pointerIndex, pointerItem, nextItem } = derivePointer(envelope.session, envelope.items);

  return {
    envelopeSessionId: envelope.session.id,
    envelopeRevision: envelope.session.revision,
    envelopeStatus: envelope.session.status,
    envelopeDeviceId: envelope.session.deviceId ?? null,
    envelopePointerIndex: pointerIndex,
    envelopePointerItemId: pointerItem?.id ?? envelope.session.currentItemId ?? null,
    envelopePointerTrackUri: pointerItem?.trackUri ?? null,
    envelopePointerMatchesSessionId:
      pointerItem && envelope.session.currentItemId
        ? pointerItem.id === envelope.session.currentItemId
        : Boolean(pointerItem) === Boolean(envelope.session.currentItemId),
    envelopeQueueLength: envelope.items.length,
    envelopeNextItemId: nextItem?.id ?? null,
    envelopeNextTrackUri: nextItem?.trackUri ?? null
  };
};

const extractRealtimePointer = (event: QueueRealtimeEvent) => ({
  realtimeSessionId: event.sessionId,
  realtimeRevision: event.revision,
  realtimePointerIndex: event.pointer.itemIndex,
  realtimePointerItemId: event.pointer.itemId ?? null,
  realtimePointerTrackUri: event.pointer.trackUri ?? null,
  realtimeSessionStatus: event.pointer.status
});

export const createQueueDiagnostics = ({
  enabled,
  getSnapshot,
  getProgressSummary,
  getProgressUpdatedAt,
  getLastPlayingState
}: BaseOptions): QueueDiagnostics => {
  const resolveSnapshot = () => toSnapshot(getSnapshot());

  const resolveProgress = () => {
    const summary = getProgressSummary?.() ?? null;
    if (!summary) return null;
    return {
      progressItemId: summary.itemId ?? null,
      progressItemIndex: summary.itemIndex,
      progressMs: summary.progressMs
    };
  };

  const resolveProgressUpdatedAt = () => {
    const timestamp = getProgressUpdatedAt?.() ?? 0;
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
  };

  const resolveLastPlayingState = () => getLastPlayingState?.() ?? null;

  const log = (level: LogLevel, event: string, details?: EventDetails) => {
    if (!enabled) return;

    const snapshot = resolveSnapshot();
    const pointer = extractPointerSnapshot(snapshot);
    const progress = resolveProgress();
    const progressUpdatedAt = resolveProgressUpdatedAt();
    const lastPlaying = resolveLastPlayingState();

    const payload = {
      event,
      timestamp: new Date().toISOString(),
      ...pointer,
      progressItemId: progress?.progressItemId ?? null,
      progressItemIndex: progress?.progressItemIndex ?? null,
      progressMs: progress?.progressMs ?? null,
      progressUpdatedAt,
      lastPlayingState: lastPlaying,
      ...(details ?? {})
    };

    const consoleMethod = console[level] ?? console.log;
    consoleMethod.call(console, NAMESPACE, payload);
  };

  return {
    logInfo: (event, details) => log('info', event, details),
    logWarn: (event, details) => log('warn', event, details),
    logError: (event, details) => log('error', event, details),
    logEnvelope: (source, envelope, details) => {
      if (!enabled) return;
      const pointerDetails = extractEnvelopePointer(envelope);
      log('info', `envelope:${source}`, { ...pointerDetails, ...(details ?? {}) });
    },
    logRealtime: (event) => {
      if (!enabled) return;
      const pointerDetails = extractRealtimePointer(event);
      log('info', `realtime:${event.event}`, pointerDetails);
    }
  };
};
